import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Modal, Carousel, Button, Spin, Input, message, QRCode } from 'antd';
import { CheckCircleFilled, TagOutlined, AlipayCircleFilled, WechatFilled, RightOutlined, LeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { BASE_URL, PROFILE_INFO_URL, SUBSCRIPTIONS_PAYMENT_URL, SUBSCRIPTIONS_PLAN_URL, SUBSCRIPTIONS_PRO_URL, SUBSCRIPTIONS_TRIAL_URL, SUBSCRIPTIONS_VIP_URL } from '../constants/Url'
import httpClient from '../utils/HttpClient';
import { debounce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { loginSuccessAction } from '../modules/auth/actions';
import { storageKeys } from '../constants/storage';
import { BsCheckCircleFill, BsCircle, BsInfo } from "react-icons/bs";

const PROModal = ({ open, onCancel, onSuccess, fetchCourse }) => {
  const { t } = useTranslation();
  const carouselRef = useRef(null);
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isPromoModalOpen, setIsPromoModalOpen] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [applyLoading, setApplyLoading] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('pro');
  const [paymentMethod, setPaymentMethod] = useState('alipay');
  const [profile, setProfile] = useState([]);
  const [couponCode, setCouponCode] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const user = useSelector((state) => state.authReducer);
  let token = user && user?.token ? user?.token : null
  const dispatch = useDispatch();

  const runTimer = useRef(null);
  const timer = useRef(null);
  const [paymentQrcode, setPaymentQrcode] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [checkPaymentStatusParam, setCheckPaymentStatusParam] = useState(null);
  const [showCloseModal, setShowCloseModal] = useState(false);

  const handleClose = () => {
    setIsPaymentModalOpen(false);
    setPaymentQrcode(null);
    setErrorMessage(null);
    setCheckPaymentStatusParam(null);
    runTimer.current = false;
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    setCouponCode("");
    setCouponDiscount(0);
    setPaymentMethod("alipay");
  }

  useEffect(() => {
    return () => {
      setPaymentQrcode(null);
      setErrorMessage(null);
      setCheckPaymentStatusParam(null);
      runTimer.current = false;
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    }
  }, []);

  const checkPaymentStatus = async (subscriptionId, method, payUrl) => {
    if (!runTimer.current) return;
    setErrorMessage(null);
    try {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = null;
      const res = await httpClient.post(`/subscriptions/payment-query`, {
        method: method,
        subscriptionId: subscriptionId
      });
      if (res.data && res.data.status === 200) {
        const order = res.data.data || res.data;
        if (order.paymentStatus === "paid") {
          runTimer.current = false;
          const successMsg = t('text_operation_success');
          message.success(successMsg);
          if (fetchCourse) fetchCourse();
          setIsPaymentModalOpen(false);
          await fetchProfiles();
          if (onSuccess) onSuccess();
          if (onCancel) onCancel();
        }
      } else {
        runTimer.current = false;
        setErrorMessage(t('text_failed_loading_order_detail'));
      }
      timer.current = setTimeout(() => {
        checkPaymentStatus(subscriptionId, method, payUrl);
      }, 3000);
    } catch (err) {
      runTimer.current = false;
      console.error('Failed to check order status:', err);
      setErrorMessage(err.message || 'Error checking order status');
    }
  };

  useEffect(() => {
    if (open) {
      fetchPlans();
    }
  }, [open]);

  const saveAuth = (token, user, refreshToken) => {
    dispatch(loginSuccessAction({ token, user: user, photo: user?.picUrl }));
    localStorage.setItem(storageKeys.TOKEN, btoa(token));
    localStorage.setItem(storageKeys.USER, JSON.stringify(user));
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const res = await httpClient.get(SUBSCRIPTIONS_PLAN_URL)
        .then(r => r.data)
        .catch(e => ({ status: 500, message: e?.message }));

      if (res && res.status === 200 && res.data) {
        setPlans(res.data);
      }
    } catch (error) {
      console.error('fetch plans error', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfiles = async () => {
    try {
      const res = await httpClient.get(PROFILE_INFO_URL);
      const member = res?.data?.member;
      const status = res?.data?.status;

      if (status === 200) {
        setProfile(member);
        saveAuth(token, member)
      } else {
        setProfile([]);
      }

    } catch (error) {
      console.error("fetchData -> error", error);
      setProfile([]);
    }
  };
  const debouncedFetchAll = useCallback(
    debounce(async () => {
      await Promise.all([fetchProfiles()])
    }, 300),
    []
  );

  useEffect(() => {
    debouncedFetchAll();
    return debouncedFetchAll.cancel;
  }, [debouncedFetchAll])

  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) return;

    try {
      setApplyLoading(true);
      const response = await httpClient.put(SUBSCRIPTIONS_TRIAL_URL, {
        planId: plans?.pro?.planId,
        promoCode: promoCode.trim()
      });

      setApplyLoading(false); // Stop loading immediately

      const res = response?.data;

      if (res && (res.status === 200 || res.status === 201 || String(res.status) === '200' || res.code === 200 || res.code === 0)) {
        const successMsg = typeof res.message === 'string' ? t(res?.message) : t('text_operation_success');
        message.success(successMsg);
        setIsPromoModalOpen(false);
        setApplyLoading(false);
        setPromoCode("");
        await fetchProfiles();
        if (onSuccess) onSuccess();
        onCancel?.();
      } else {
        const errorMsg = typeof res?.message === 'string' ? t(res?.message) : (t(res?.msg || 'text_operation_failed'));
        message.error(errorMsg);
      }
    } catch (error) {
      console.error('apply promo code error', error);
      setApplyLoading(false);

      const errorData = error?.response?.data || error;
      const errorMsg = typeof errorData?.message === 'string' ? t(errorData?.message) : t('text_operation_failed');
      message.error(errorMsg);
    }
  };

  const handleClearCoupon = () => {
    setCouponDiscount(0);
    setCouponCode('');
    message.success(t('text_coupon_cancelled'));
  };

  //Apply Coupon
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      message.error(t('text_input_coupon'));
      return;
    }
    setApplyingCoupon(true);
    try {
      const res = await httpClient.post('/coupons-scan-validate', {
        couponCode: couponCode.trim(),
        currentOrderTotal: selectedPlan === "pro" ? plans?.pro?.amount : plans?.super?.amount
      });
      if (res?.data?.status === 200) {
        const data = res?.data?.data || res?.data;
        const discount = data?.discountAmount || data?.discount || 0;
        message.success(t("text_coupon_success"));
        setCouponDiscount(discount);
      } else {
        message.error(t(res?.data?.message));
        setCouponCode("")
      }
    } catch (err) {
      message.error(t(err?.response?.data?.message || err?.message || 'text_coupon_failed'));
      setCouponCode("")
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleConfirmPayment = async () => {
    try {
      setApplyLoading(true);

      const url = selectedPlan === 'pro' ? SUBSCRIPTIONS_PRO_URL : SUBSCRIPTIONS_VIP_URL;
      const planId = selectedPlan === 'pro' ? plans?.pro?.planId : plans?.super?.planId;

      // 1. Call order creation API
      const proResponse = await httpClient.put(url, {
        planId: planId,
        method: paymentMethod,
        couponCode:couponCode
      });

      const proRes = proResponse?.data;

      if (proRes && (proRes.status === 200 || proRes.status === 201 || String(proRes.status) === '200' || proRes.code === 200 || proRes.code === 0)) {

        const transactionData = proRes.data || proRes;
        const payUrl = transactionData?.paymentUrl || transactionData?.payUrl || transactionData?.redirectUrl || transactionData?.url || proRes?.paymentUrl || proRes?.payUrl;

        // If backend returns an external payment gateway URL (Alipay/WeChat), redirect user to pay
        if (payUrl) {
          window.location.href = payUrl;
          return;
        }

        var code_url = null;
        if (paymentMethod === 'alipay') {
          code_url = transactionData?.orderinfo?.qrCode;
        } else if (paymentMethod === 'wechatpay') {
          code_url = transactionData?.orderinfo?.code_url;
        }
        setPaymentQrcode(code_url);

        if (code_url) {
          runTimer.current = true;
          setCheckPaymentStatusParam({
            subscriptionId: transactionData.subscriptionId,
            method: paymentMethod,
            payUrl: payUrl
          });
          checkPaymentStatus(transactionData.subscriptionId, paymentMethod, payUrl);
        }
      } else {
        const errorMsg = typeof proRes?.message === 'string' ? t(proRes.message) : (t(proRes?.msg) || t('text_operation_failed'));
        message.error(errorMsg);
      }
    } catch (error) {
      console.error('payment error', error);

      const errorData = error?.response?.data || error;
      const errorMsg = typeof errorData?.message === 'string' ? t(errorData.message) : (error?.message || t('text_operation_failed'));
      message.error(errorMsg);
    } finally {
      setApplyLoading(false);
    }
  };

  const [currentSlide, setCurrentSlide] = useState(0);

  const formatUnit = (unit) => {
    if (unit === 'month') return t('text_month');
    if (unit === 'year' || unit === 'years') return t('text_year');
    if (unit === 'day' || unit === 'days') return t('text_day_no');
    return t('text_year');
  };

  const isSuperEnabled = Boolean(plans?.super && !plans?.super?.disabled && plans?.super?.status !== false);
  console.log("plans", plans)
  const hasTrial = Boolean(plans?.trial);

  // const trialSlideIndex = hasTrial ? 0 : -1;
  const normalSlideIndex = 0;
  const superSlideIndex = normalSlideIndex ? (isSuperEnabled ? 0 : 1) : (isSuperEnabled ? 1 : -1);

  const trialFeatures = [
    t('text_trail_benefit_1'),
    t('text_trail_benefit_2'),
    t('text_trail_benefit_3'),
    t('text_trail_benefit_4')
  ];

  const regularFeatures = [
    t('text_pro_benefit_1'),
    t('text_pro_benefit_2'),
    t('text_pro_benefit_3'),
    t('text_pro_benefit_4'),
  ];

  const superFeatures = [
    t('text_svip_benefit_1'),
    t('text_svip_benefit_2'),
    t('text_svip_benefit_3'),
    t('text_svip_benefit_4'),
  ];

  return (
    <Modal
      title={<div className="text-center text-xl font-bold py-3">{t('text_activate_member')}</div>}
      open={open}
      onCancel={onCancel}
      footer={null}
      width={510}
      centered
      className="vip-subscription-modal"
      styles={{ body: { padding: '10px 0 24px 0' } }}
    >
      <Spin spinning={loading}>
        <Carousel
          ref={carouselRef}
          dotPosition="bottom"
          afterChange={(current) => setCurrentSlide(current)}
          // style={{ paddingBottom: '36px' }}
          dots={false}
        >
          {/* Slide 1: Free Trial */}
          {/* {hasTrial && (
            <div className="px-5 py-2">
              <div className="relative flex items-center justify-center h-[390px]">
                <div className="w-[370px] mr-8 border border-[#e91e63] rounded-2xl p-7 bg-white relative h-[390px] flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-center gap-3 mb-8">
                      <span className="font-serif text-2xl font-bold tracking-widest">
                        <img alt='ESI-Logo' role='button' src={BASE_URL + "/logo.svg"} className='esi-logo' />
                      </span>
                    </div>

                    <div className="space-y-3">
                      {trialFeatures.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <CheckCircleFilled className="text-[#e91e63] mt-1 shrink-0" />
                          <span className="text-gray-700 text-sm font-medium">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4 text-center">
                    <Button
                      type="primary"
                      onClick={() => setIsPromoModalOpen(true)}
                      className="w-full bg-[#e91e63] hover:bg-[#d81557] border-none h-[44px] rounded-lg text-base font-bold mb-3 cursor-pointer"
                    >
                      {t('text_activate_now')}
                    </Button>
                    <div className="text-gray-500 text-sm">
                      {t("text_free")} {plans?.trial?.count ?? 0} / {formatUnit(plans?.trial?.unit)}
                    </div>
                  </div>
                </div>

                
                <div
                  onClick={() => carouselRef.current?.goTo(normalSlideIndex)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 h-[384px] w-[55px] bg-[#fcf3f5] border border-[#e91e63] rounded-r-2xl rounded-l-md flex flex-col items-center justify-between py-6 px-1.5 cursor-pointer transition-all duration-300 hover:brightness-95 group z-10"
                  title={t('text_member')}
                >
                  <span className="bg-[#fce4ec] text-[#e91e63] text-xs font-black px-2.5 py-1 rounded-full tracking-wider">
                    PRO
                  </span>
                  <span
                    className="text-[#e91e63] text-[13px] font-bold tracking-widest select-none leading-relaxed"
                    style={{ writingMode: 'vertical-rl', letterSpacing: '3px' }}
                  >
                    {t('text_member')}
                  </span>
                  <div className="w-8 h-8 rounded-full bg-[#e91e63] text-white flex items-center justify-center shadow-md group-hover:scale-115 transition-transform">
                    <RightOutlined className="text-sm font-black" />
                  </div>
                </div>
              </div>
            </div>
          )} */}

          {/* Slide 2: Regular / Normal Membership */}
          <div className="px-5 py-2">
            <div className="relative flex items-center justify-center h-[390px]">
              {/* {hasTrial && (
                <div
                  onClick={() => carouselRef.current?.goTo(trialSlideIndex)}
                  className="absolute left-0 top-1/2 -translate-y-1/2 h-[384px] w-[55px] bg-white border border-[#e91e63] rounded-l-2xl rounded-r-md flex flex-col items-center justify-between py-6 px-1.5 cursor-pointer transition-all duration-300 hover:brightness-95 group z-10"
                  title={t('text_free_trial')}
                >
                  <span className="bg-[#fce4ec] text-[#e91e63] text-xs font-black px-2.5 py-1 rounded-full shadow-sm tracking-wider">
                    {t('text_free')}
                  </span>
                  <span
                    className="text-[#e91e63] text-[13px] font-bold tracking-widest select-none leading-relaxed"
                    style={{ writingMode: 'vertical-rl', letterSpacing: '3px' }}
                  >
                    {t('text_free_trial')}
                  </span>
                  <div className="w-8 h-8 rounded-full bg-[#e91e63] text-white flex items-center justify-center shadow-md group-hover:scale-115 transition-transform">
                    <LeftOutlined className="text-sm font-black" />
                  </div>
                </div>
              )} */}

              {/* Main Normal Membership Card */}
              <div
                className={`${isSuperEnabled ? 'w-[370px] mr-8' : (normalSlideIndex ? 'w-[370px] ml-8' : 'w-full')} h-[390px] border border-[#e91e63] rounded-2xl p-7 bg-[#fcf3f5] relative shadow-sm flex flex-col justify-between overflow-hidden`}
              >
                <div>
                  <div className="flex items-center justify-center gap-3 mb-6">
                    <span className="font-serif text-2xl font-bold tracking-widest">
                      <img alt='ESI-Logo' role='button' src={BASE_URL + "/logo.svg"} className='esi-logo' />
                    </span>
                    <span className="bg-[#fce4ec] text-[#e91e63] text-xs px-2 py-0.5 rounded-full font-bold">
                      {t('text_member')}
                    </span>
                  </div>

                  <div className="space-y-3">
                    {regularFeatures.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <CheckCircleFilled className="text-[#e91e63] mt-1 shrink-0" />
                        <span className="text-gray-700 text-sm font-medium">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4 text-center">
                  <Button
                    type="primary"
                    onClick={() => {
                      setSelectedPlan('pro');
                      setIsPaymentModalOpen(true);
                    }}
                    className="w-full bg-[#e91e63] hover:bg-[#d81557] border-none h-[44px] rounded-lg text-base font-bold mb-3 cursor-pointer"
                  >
                    {t('text_activate_now')}
                  </Button>
                  <div className="text-gray-500 text-sm">
                    ¥ {plans?.pro?.amount ?? 0} / {formatUnit(plans?.pro?.unit)}
                  </div>
                </div>
              </div>

              {/* Right Peek Tab -> Super VIP (if enabled) */}
              {isSuperEnabled && (
                <div
                  onClick={() => carouselRef.current?.goTo(superSlideIndex)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 h-[384px] w-[55px] bg-gradient-to-b from-[#b82956] to-[#8d1d42] border border-[#d81557] rounded-r-2xl rounded-l-md flex flex-col items-center justify-between py-6 px-1.5 cursor-pointer transition-all duration-300 hover:brightness-110 group z-10"
                  title={t('text_svip_member')}
                >
                  <span className="bg-[#ffe58f] text-[#8d1d42] text-xs font-black px-2.5 py-1 rounded-full shadow-sm tracking-wider">
                    VIP
                  </span>
                  <span
                    className="text-white text-[13px] font-bold tracking-widest select-none leading-relaxed"
                    style={{ writingMode: 'vertical-rl', letterSpacing: '3px' }}
                  >
                    {t('text_svip_member')}
                  </span>
                  <div className="w-8 h-8 rounded-full bg-white text-[#8d1d42] flex items-center justify-center shadow-md group-hover:scale-115 transition-transform">
                    <RightOutlined className="text-sm font-black" />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Slide 3: Super VIP */}
          {isSuperEnabled && (
            <div className="px-5 py-2">
              <div className="relative flex items-center justify-center h-[390px]">
                {/* Left Peek Tab -> Normal Membership */}
                <div
                  onClick={() => carouselRef.current?.goTo(normalSlideIndex)}
                  className="absolute left-0 top-1/2 -translate-y-1/2 h-[384px] w-[55px] bg-[#fcf3f5] border border-[#e91e63] rounded-l-2xl rounded-r-md flex flex-col items-center justify-between py-6 px-1.5 cursor-pointer transition-all duration-300 hover:brightness-95 group z-10"
                  title={t('text_member')}
                >
                  <span className="bg-[#fce4ec] text-[#e91e63] text-xs font-black px-2.5 py-1 rounded-full shadow-sm tracking-wider">
                    PRO
                  </span>
                  <span
                    className="text-[#e91e63] text-[13px] font-bold tracking-widest select-none leading-relaxed"
                    style={{ writingMode: 'vertical-rl', letterSpacing: '3px' }}
                  >
                    {t('text_member')}
                  </span>
                  <div className="w-8 h-8 rounded-full bg-[#e91e63] text-white flex items-center justify-center shadow-md group-hover:scale-115 transition-transform">
                    <LeftOutlined className="text-sm font-black" />
                  </div>
                </div>

                {/* Main Super VIP Card */}
                <div className="w-[370px] ml-8 h-[390px] border border-[#d81557] rounded-2xl p-7 bg-[#cf3d6b] relative shadow-sm flex flex-col justify-between text-white overflow-hidden">
                  <div>
                    <div className="flex items-center justify-center gap-3 mb-6">
                      <span className="text-white font-serif text-2xl font-bold tracking-widest">
                        <img alt='ESI-Logo' role='button' src={BASE_URL + "/logo.svg"} className='esi-logo brightness-0 invert' />
                      </span>
                      <span className="bg-[#ffe58f] text-[#cf3d6b] text-xs px-2 py-0.5 rounded-full font-bold">
                        {t('text_svip_member')}
                      </span>
                    </div>

                    <div className="space-y-3">
                      {superFeatures.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <CheckCircleFilled className="text-white mt-1 shrink-0" />
                          <span className="text-white text-sm font-medium">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4 text-center">
                    <Button
                      onClick={() => {
                        setSelectedPlan('super');
                        setIsPaymentModalOpen(true);
                      }}
                      className="w-full bg-white hover:bg-gray-100 text-[#cf3d6b] border-none h-[44px] rounded-lg text-base font-bold mb-3 cursor-pointer"
                    >
                      {t('text_activate_now')}
                    </Button>
                    <div className="text-white text-sm opacity-90">
                      ¥ {plans?.super?.amount ?? 0} / {formatUnit(plans?.super?.unit)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Carousel>
      </Spin>

      {/* <Modal
        open={isPromoModalOpen}
        onCancel={() => {
          setIsPromoModalOpen(false);
          setPromoCode('');
        }}
        footer={null}
        title={null}
        closable={true}
        width={380}
        centered
        styles={{ body: { padding: '24px', borderRadius: '12px' } }}
      >
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2 text-[15px] font-semibold text-gray-800">
            <TagOutlined className="text-gray-500 text-lg transform -scale-x-100" />
            <span>{t('促销码')}</span>
          </div>
          <div className="flex w-full mt-1 gap-2">
            <Input 
              placeholder={t('Enter code')} 
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              className="flex-1 rounded-md border-gray-300 h-10 hover:border-gray-400 focus:border-gray-400"
            />
            <Button 
              type="primary" 
              loading={applyLoading}
              disabled={!promoCode.trim()}
              className={`h-10 px-6 rounded-md font-medium ${promoCode.trim() ? '' : 'bg-gray-300 text-gray-100'}`}
              onClick={handleApplyPromoCode}
            >
              {t('使用')}
            </Button>
          </div>
        </div>
      </Modal> */}

      <Modal
        open={isPaymentModalOpen}
        onCancel={() => {
          if (paymentQrcode) {
            setShowCloseModal(true);
          } else {
            handleClose();
          }
        }}
        footer={null}
        title={null}
        closable={true}
        width={450}
        centered
        styles={{ body: { padding: '24px', borderRadius: '12px' } }}
      >
        {paymentQrcode ? (
          <>
          <div className="flex flex-col items-center justify-center py-2">
            <div className="text-lg font-bold text-gray-800 text-center mb-4">{t('text_tobepaid_qrcode')}</div>
            <QRCode
              value={paymentQrcode}
              size={220}
              icon={paymentMethod === 'wechatpay' ? "https://ts4.tc.mm.bing.net/th/id/ODF.BvtHqZTl6qLypPDIASUGoA?w=32&h=32&qlt=97&pcl=fffffa&o=6&pid=1.2" : "https://ts1.tc.mm.bing.net/th/id/ODF.tFQwCWKfvMeJYOFxHknauw?w=32&h=32&qlt=90&pcl=fffffa&o=6&pid=1.2"}
              iconSize={{
                width: 32,
                height: 32,
              }}
            />
          </div>
          <div style={{marginTop: 10, marginBottom: errorMessage ? 10 : 0, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 5}}>
            <div className="bg-[#fff0f4] p-0.5 rounded-full">
              <BsInfo className="text-[#e80d44] text-xl shrink-0" />
            </div>
            <span className="text-[#e80d44] text-sm font-medium">{t("text_qrcode_desc")}</span>
          </div>
          {errorMessage ? <div className="flex flex-col items-center justify-center w-full">
            <span style={{color: 'red'}} className="text-sm text-gray-500 font-medium">{errorMessage}</span>

            <button
              onClick={() => {
                checkPaymentStatus(checkPaymentStatusParam.orderId, checkPaymentStatusParam.method, checkPaymentStatusParam.payUrl);
              }}
              style={{width: '100%'}}
              className={`rounded-xl bg-gradient-to-r mt-4 from-[#e80d44] to-[#ff3b68] text-white px-10 py-3 font-semibold text-sm transition-all duration-300 flex justify-center items-center gap-2 hover:scale-105 active:scale-95`}
            >
              {t("text_retry")}
            </button>
          </div> : null}
          </>
        ) : (
          <div className="flex flex-col gap-4 mt-2">
            <div className="text-lg font-bold text-gray-800 text-center mb-2">{t('text_select_payment')}</div>

            <div
              className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${paymentMethod === 'alipay' ? 'border-[#e91e63] bg-[#fdf2f5]' : 'border-gray-100 hover:border-gray-200'}`}
              onClick={() => setPaymentMethod('alipay')}
            >
              <div className="flex items-center gap-3">
                <AlipayCircleFilled className="text-[#1677ff] text-[28px]" />
                <span className="font-semibold text-gray-800 text-base">{t('text_alipay_payment')}</span>
              </div>
              {paymentMethod === 'alipay' ? <CheckCircleFilled className="text-[#52c41a] text-xl" /> : <div className="w-[20px] h-[20px] rounded-full border-2 border-gray-300"></div>}
            </div>

            <div
              className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${paymentMethod === 'wechatpay' ? 'border-[#e91e63] bg-[#fdf2f5]' : 'border-gray-100 hover:border-gray-200'}`}
              onClick={() => setPaymentMethod('wechatpay')}
            >
              <div className="flex items-center gap-3">
                <WechatFilled className="text-[#09b83e] text-[28px]" />
                <span className="font-semibold text-gray-800 text-base">{t('text_wechat_payment')}</span>
              </div>
              {paymentMethod === 'wechatpay' ? <CheckCircleFilled className="text-[#52c41a] text-xl" /> : <div className="w-[20px] h-[20px] rounded-full border-2 border-gray-300"></div>}
            </div>

            {/* Coupon Section */}
            {
              couponDiscount > 0 ? "" :
              <>
              <h3 className="text-sm font-semibold text-gray-900">{t("text_use_coupon")}</h3>
            <div className="flex gap-2" style={{marginTop:"-0.5rem"}}>
              <input
                type="text"
                placeholder={t("text_input_coupon")}
                className="flex-1 border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-200"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
              />
              <button
                onClick={handleApplyCoupon}
                disabled={applyingCoupon}
                className="bg-[#e80d44] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#d00b3c] transition-colors disabled:opacity-50"
              >
                {applyingCoupon ? t('text_applying') : t('text_exchange')}
              </button>
            </div>
            </>
            }

            <div className="space-y-3 bg-[#fcfcfc] border border-gray-100 rounded-xl p-4 mt-2 shadow-sm">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-500">{t("text_product_total")}</span>
                <span className="text-gray-900 font-medium">¥{Number((selectedPlan === 'pro' ? plans?.pro?.amount : plans?.super?.amount) || 0).toFixed(2)}</span>
              </div>

              {couponDiscount > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500">{t("text_coupon_deduct")}</span>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 bg-[#f7f8fc] border border-dashed border-[#d4d8ef] text-[#5e6db0] px-2.5 py-1 rounded-md shadow-sm"  style={{marginRight:"2rem"}}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="1.1em" height="1.1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/>
                        <path d="M9 5v14"/>
                      </svg>
                      <span className="text-xs font-bold tracking-wide">{couponCode}</span>
                      <button
                        onClick={handleClearCoupon}
                        className="hover:text-[#e80d44] transition-colors ml-1 cursor-pointer flex items-center justify-center"
                        title={t("text_cancel_coupon")}
                      >
                        <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="1.1em" width="1.1em"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                      </button>
                    </div>
                    <span className="text-[#e80d44] font-medium">-¥{couponDiscount?.toFixed(2)}</span>
                  </div>
                </div>
              )}

              <div className="pt-3 border-t border-gray-100 flex justify-between items-center">
                <span className="text-gray-600 font-medium">{t("text_tobepaid_amount")}</span>
                <span className="text-[#e80d44] text-2xl font-bold tracking-tight">¥{Math.max(0, ((selectedPlan === 'pro' ? plans?.pro?.amount : plans?.super?.amount) || 0) - couponDiscount).toFixed(2)}</span>
              </div>
            </div>

            <div>
              <Button
                type="primary"
                loading={applyLoading}
                className="w-full bg-[#e91e63] hover:bg-[#d81557] border-none h-[44px] rounded-lg text-base font-bold mt-4"
                onClick={handleConfirmPayment}
              >
                {t('text_confirm_payment')}
              </Button>
            </div>
          </div>
        )}
        <Modal
          open={showCloseModal}
          onCancel={() => {
            setShowCloseModal(false);
          }}
          closeable={true}
          footer={null}
          centered
          width={400}
          title={null}
          styles={{
            body: {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px 16px 32px 16px',
            }
          }}
        >
          <div className="flex flex-col items-center justify-center p-2">
            <div style={{padding: 12}} className="bg-[#fff0f4] rounded-full">
              <BsInfo size={36} color="#e80d44" />
            </div>
            <span className="text-sm shrink-0 font-medium mt-4">{t("text_qrcode_warning")}</span>
            <button
              onClick={() => {
                setShowCloseModal(false);
                setTimeout(() => {
                  handleClose();
                }, 100);
              }}
              style={{width: '100%'}}
              className={`rounded-xl bg-gradient-to-r mt-4 from-[#e80d44] to-[#ff3b68] text-white px-10 py-3 font-semibold text-sm transition-all duration-300 flex justify-center items-center gap-2 hover:scale-105 active:scale-95`}
            >
              {t("text_qrcode_confirm")}
            </button>
          </div>
        </Modal>
      </Modal>

      <style jsx global>{`
        .vip-subscription-modal .ant-modal-content {
          border-radius: 16px;
          overflow: hidden;
        }
        .vip-subscription-modal .ant-carousel .slick-dots li button {
          background: #d9b1d1;
          height: 8px;
          width: 8px;
          border-radius: 50%;
        }
        .vip-subscription-modal .ant-carousel .slick-dots li.slick-active button {
          background: #e91e63;
          width: 16px;
          border-radius: 10px;
        }
      `}</style>
    </Modal>
  );
};

export default PROModal;
